import { Box, Fieldset, SimpleGrid, Stack, Text } from '@chakra-ui/react'
import type { FC } from 'react'
import { memo } from 'react'

import useGetObject from '@app/hooks/useGetObject'
import useGetObjectsByProperties from '@app/hooks/useGetObjectsByProperties'
import RichTextInput from '@app/next/forms/inline/richTextInput'
import EmbeddedAiChat from '@app/pages/strategies/components/aiChat/contexts/embeddedAiChat'
import type { DomainBusinessContext } from '@app/types'
import { PipelineEnum } from '@graphql/types'

interface Props {
  businessContextId: string
  editMode?: boolean
}

const BusinessContext: FC<Props> = ({ businessContextId, editMode = false }) => {
  const businessContextById = useGetObject(businessContextId, 'businessContext') as DomainBusinessContext
  const businessContextBySlug = useGetObjectsByProperties('businessContext', {
    slug: businessContextId
  }) as DomainBusinessContext[]

  const businessContext = businessContextById || businessContextBySlug[0]

  const columns = editMode ? { base: 1, xl: 2 } : { base: 1 }

  return (
    <SimpleGrid gap={4} columns={columns} minH="50vh">
      <Fieldset.Root
        borderWidth={1}
        borderStyle="solid"
        borderColor="border.default"
        borderRadius="md"
        px={6}
        py={4}
        bg="bg"
        order={{ base: 2, xl: 1 }}
      >
        <Fieldset.Content>
          <Box position="relative" minH="50vh">
            <RichTextInput
              domainObject={businessContext}
              name="content"
              placeholder="Add a description of what you're trying to achieve"
            />
          </Box>
        </Fieldset.Content>
      </Fieldset.Root>
      {businessContext && editMode && (
        <Stack
          maxH="80vh"
          bg="bg.panel"
          px={6}
          py={4}
          borderRadius="md"
          overflowY="auto"
          borderStyle="solid"
          borderColor="border.default"
          borderWidth={1}
          gap={4}
          order={{ base: 1, xl: 2 }}
        >
          <Text fontSize="md" fontWeight="normal" textAlign="center">
            Chat with our AI agent to modify the business context.
          </Text>
          <EmbeddedAiChat
            chattableType="businessContext"
            chattableId={businessContext.id}
            pipelineId={PipelineEnum.BusinessContextChat}
            maxH="80vh"
            flexGrow={1}
            w="full"
            borderColor="border.default"
            borderWidth={1}
            borderRadius="md"
          />
        </Stack>
      )}
    </SimpleGrid>
  )
}

export default memo(BusinessContext)
